/**
 * Sesión del cliente: token en memoria + perfil + permisos efectivos.
 *
 * El rol y los permisos NUNCA se cachean para decidir autorización: se leen de `GET /me`
 * en cada carga y se re-verifican cuando el token se renueva (§9.1). La UI solo los usa
 * para mostrar u ocultar navegación; el servidor es quien decide.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiProblem, OfflineError, setAccessToken } from './api.js';
import { drainOutbox } from './outbox.js';
import { getSetting, setSetting } from './db.js';
import type { MeResponse, AuthSessionResponse, VerifyOtpBody } from '@thepit/contracts';

export interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: MeResponse | null;
  error: string | null;
}

export interface AuthApi extends AuthState {
  requestOtp: (email: string) => Promise<{ devCode?: string }>;
  verifyOtp: (body: VerifyOtpBody) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithAccessCode: (code: string, label?: string) => Promise<void>;
  rotateAccessCode: (label?: string) => Promise<{ code: string; rotatedAt: string }>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthApi | null>(null);

const TERMINAL_KEY = 'terminal';
const REFRESH_TOKEN_KEY = 'pit_refresh_token';

function getStoredRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredRefreshToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    /* ignore storage errors */
  }
}


export interface TerminalIdentity {
  id: string;
  label: string;
  kind: 'ventanilla' | 'caja' | 'hud' | 'movil';
}

export async function getTerminalIdentity(): Promise<TerminalIdentity> {
  const raw = await getSetting(TERMINAL_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as TerminalIdentity;
    } catch {
      /* valor corrupto: se regenera */
    }
  }
  const identity: TerminalIdentity = { id: `movil-${Date.now().toString(36)}`, label: 'TERMINAL', kind: 'movil' };
  await setSetting(TERMINAL_KEY, JSON.stringify(identity));
  return identity;
}

export async function setTerminalIdentity(identity: TerminalIdentity): Promise<void> {
  await setSetting(TERMINAL_KEY, JSON.stringify(identity));
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading', me: null, error: null });
  const bootstrapped = useRef(false);

  const refreshMe = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/api/v1/me');
      setState({ status: 'authenticated', me, error: null });
    } catch (error) {
      if (error instanceof ApiProblem && error.status === 401) {
        setAccessToken(null);
        setState({ status: 'anonymous', me: null, error: null });
        return;
      }
      if (error instanceof OfflineError) {
        setState((prev) => ({ ...prev, error: 'offline' }));
        return;
      }
      setState({ status: 'anonymous', me: null, error: (error as Error).message });
    }
  }, []);

  const acceptSession = useCallback((session: AuthSessionResponse) => {
    setAccessToken(session.accessToken);
    if (session.refreshToken) {
      setStoredRefreshToken(session.refreshToken);
    }
    setState({
      status: 'authenticated',
      me: {
        user: session.user,
        org: session.org,
        permissions: session.permissions,
        server: session.server,
      },
      error: null,
    });
    void drainOutbox();
  }, []);

  // Al montar: intenta renovar con la cookie de refresh o token persistido (sesión garantizada en F5).
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void (async () => {
      const storedToken = getStoredRefreshToken();
      // Sin token persistido no hay nada que renovar: no preguntemos a la API
      // (evita un 400 espurio en la consola y un request inútil en modo offline).
      if (!storedToken) {
        setState({ status: 'anonymous', me: null, error: null });
        return;
      }
      try {
        const session = await api.post<AuthSessionResponse>(
          '/api/v1/auth/refresh',
          { refreshToken: storedToken },
          { skipRefresh: true },
        );
        acceptSession(session);
      } catch {
        setStoredRefreshToken(null);
        setAccessToken(null);
        setState({ status: 'anonymous', me: null, error: null });
      }
    })();
  }, [acceptSession]);

  // Drena el outbox al volver la conexión.
  useEffect(() => {
    const onOnline = (): void => {
      void drainOutbox();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const requestOtp = useCallback(async (email: string) => {
    const res = await api.post<{ sent: true; expiresInSeconds: number; devCode?: string }>(
      '/api/v1/auth/otp/request',
      { email },
    );
    return { devCode: res.devCode };
  }, []);

  const verifyOtp = useCallback(async (body: VerifyOtpBody) => {
    const session = await api.post<AuthSessionResponse>('/api/v1/auth/otp/verify', body);
    acceptSession(session);
  }, []);



  const loginWithPassword = useCallback(async (email: string, password: string) => {
    const session = await api.post<AuthSessionResponse>(
      '/api/v1/auth/password',
      { email: email.trim().toLowerCase(), password },
      { skipRefresh: true },
    );
    acceptSession(session);
  }, [acceptSession]);

  const loginWithAccessCode = useCallback(async (code: string, label?: string) => {
    const session = await api.post<AuthSessionResponse>(
      '/api/v1/auth/access-code',
      { code, label },
      { skipRefresh: true },
    );
    acceptSession(session);
  }, [acceptSession]);

  const rotateAccessCode = useCallback(async (label?: string) => {
    return api.post<{ code: string; rotatedAt: string }>(
      '/api/v1/admin/access-codes/rotate',
      { label },
    );
  }, []);

  const logout = useCallback(async () => {
    const storedToken = getStoredRefreshToken();
    try {
      await api.post('/api/v1/auth/logout', storedToken ? { refreshToken: storedToken } : undefined);
    } catch {
      /* el logout local procede igual */
    }
    setStoredRefreshToken(null);
    setAccessToken(null);
    setState({ status: 'anonymous', me: null, error: null });
  }, []);

  const hasPermission = useCallback(
    (permission: string) => Boolean(state.me?.permissions.includes(permission as never)),
    [state.me],
  );

  const value = useMemo<AuthApi>(
    () => ({ ...state, requestOtp, verifyOtp, loginWithPassword, loginWithAccessCode, rotateAccessCode, logout, refreshMe, hasPermission }),
    [state, requestOtp, verifyOtp, loginWithPassword, loginWithAccessCode, rotateAccessCode, logout, refreshMe, hasPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}

/** ¿El usuario puede ver esta ruta? Deriva de la matriz SSOT que devuelve la API. */
export function useCan(): (resource: string, action: string) => boolean {
  const { hasPermission } = useAuth();
  return useCallback((resource: string, action: string) => hasPermission(`${resource}:${action}`), [hasPermission]);
}
